import { useEffect, useState } from "react";
import { Document, Page, Text, View, StyleSheet, Image, Font, pdf } from "@react-pdf/renderer";
import {
  Bug,
  ChevronDown,
  Download,
  Flame,
  Lightbulb,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  X,
  StickyNote,
} from "lucide-react";
import logoUrl from "@/assets/logo_new.png";
import BrandLogo from "@/components/BrandLogo";
import { api, type ApiClient } from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyPanel, PageHero, PagePanel, PageShell, PanelHeading } from "@/components/interior-page";

// ── Types ─────────────────────────────────────────────────────────────
interface ReleaseNote {
  id: number;
  version: string;
  title: string;
  released_at: string;
  whats_new: string[];
  improvements: string[];
  bug_fixes: string[];
  note: string | null;
  created_at: string;
}

// ── PDF styles ────────────────────────────────────────────────────────
const NAVY  = "#0F2554";
const WHITE = "#FFFFFF";
const GREEN = "#16A34A";
const BLUE  = "#2563EB";
const RED   = "#DC2626";
const AMBER = "#D97706";
const GREY  = "#64748B";
const BORDER= "#E2E8F0";

const ps = StyleSheet.create({
  // paddingBottom reserves a band on every page for the fixed footer so flowing
  // content never collides with it when notes span multiple pages.
  page:        { backgroundColor: WHITE, padding: 0, paddingBottom: 52, fontFamily: "Helvetica" },
  header:      { backgroundColor: NAVY, paddingHorizontal: 32, paddingVertical: 20, flexDirection: "row", alignItems: "center", justifyContent: "space-between" },
  logoPill:    { backgroundColor: WHITE, borderRadius: 8, paddingHorizontal: 10, paddingVertical: 6 },
  logo:        { width: 110, height: 28, objectFit: "contain" },
  headerRight: { alignItems: "flex-end" },
  headerLabel: { color: "#93C5FD", fontSize: 8, fontFamily: "Helvetica", letterSpacing: 1.5, textTransform: "uppercase" },
  headerTitle: { color: WHITE, fontSize: 20, fontFamily: "Helvetica-Bold", marginTop: 2 },
  body:        { paddingHorizontal: 32, paddingTop: 28, paddingBottom: 12 },
  meta:        { marginBottom: 24, paddingBottom: 16, borderBottomWidth: 1, borderBottomColor: BORDER },
  metaTop:     { flexDirection: "row", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" },
  metaVersion: { backgroundColor: NAVY, borderRadius: 12, paddingHorizontal: 10, paddingVertical: 4 },
  metaVersionTxt: { color: WHITE, fontSize: 10, fontFamily: "Helvetica-Bold" },
  metaDate:    { color: GREY, fontSize: 10 },
  metaTitle:   { color: NAVY, fontSize: 16, fontFamily: "Helvetica-Bold", lineHeight: 1.3 },
  section:     { marginBottom: 20 },
  sectionHead: { flexDirection: "row", alignItems: "center", gap: 6, marginBottom: 8 },
  sectionDot:  { width: 8, height: 8, borderRadius: 4 },
  sectionLbl:  { fontSize: 11, fontFamily: "Helvetica-Bold", letterSpacing: 0.5 },
  item:        { flexDirection: "row", gap: 8, marginBottom: 5, paddingLeft: 4 },
  bullet:      { width: 4, height: 4, borderRadius: 2, marginTop: 5 },
  itemTxt:     { fontSize: 10, color: "#334155", flex: 1, lineHeight: 1.6 },
  noteBox:     { backgroundColor: "#FFFBEB", borderRadius: 8, padding: 12, flexDirection: "row", gap: 8, marginTop: 8 },
  noteTxt:     { fontSize: 9.5, color: "#92400E", flex: 1, lineHeight: 1.5 },
  footer:      { position: "absolute", bottom: 0, left: 0, right: 0, backgroundColor: WHITE, borderTopWidth: 1, borderTopColor: BORDER, paddingHorizontal: 32, paddingVertical: 12, flexDirection: "row", justifyContent: "space-between", alignItems: "center" },
  footerTxt:   { color: GREY, fontSize: 8 },
});

// Disable hyphenation so words wrap whole — keeps long titles/items clean.
Font.registerHyphenationCallback((word) => [word]);

// Convert the bundled logo asset to a base64 data URI so @react-pdf can embed
// it synchronously — passing the raw Vite URL leads to it being dropped.
async function loadLogoDataUri(): Promise<string> {
  const res = await fetch(logoUrl);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function ReleaseNotePDF({ rn, logo }: { rn: ReleaseNote; logo: string }) {
  const dateStr = new Date(rn.released_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  return (
    <Document>
      <Page size="A4" style={ps.page}>
        {/* Header */}
        <View style={ps.header}>
          <View style={ps.logoPill}>
            <Image src={logo} style={ps.logo} />
          </View>
          <View style={ps.headerRight}>
            <Text style={ps.headerLabel}>Release Notes</Text>
            <Text style={ps.headerTitle}>v{rn.version}</Text>
          </View>
        </View>

        <View style={ps.body}>
          {/* Meta — version + date on top row, title on its own full-width line */}
          <View style={ps.meta}>
            <View style={ps.metaTop}>
              <View style={ps.metaVersion}><Text style={ps.metaVersionTxt}>v{rn.version}</Text></View>
              <Text style={ps.metaDate}>{dateStr}</Text>
            </View>
            <Text style={ps.metaTitle}>{rn.title}</Text>
          </View>

          {/* What's New */}
          {rn.whats_new.length > 0 && (
            <View style={ps.section}>
              <View style={ps.sectionHead}>
                <View style={[ps.sectionDot, { backgroundColor: GREEN }]} />
                <Text style={[ps.sectionLbl, { color: GREEN }]}>What's New</Text>
              </View>
              {rn.whats_new.map((item, i) => (
                <View key={i} style={ps.item}>
                  <View style={[ps.bullet, { backgroundColor: GREEN }]} />
                  <Text style={ps.itemTxt}>{item}</Text>
                </View>
              ))}
            </View>
          )}

          {/* Improvements */}
          {rn.improvements.length > 0 && (
            <View style={ps.section}>
              <View style={ps.sectionHead}>
                <View style={[ps.sectionDot, { backgroundColor: BLUE }]} />
                <Text style={[ps.sectionLbl, { color: BLUE }]}>Improvements</Text>
              </View>
              {rn.improvements.map((item, i) => (
                <View key={i} style={ps.item}>
                  <View style={[ps.bullet, { backgroundColor: BLUE }]} />
                  <Text style={ps.itemTxt}>{item}</Text>
                </View>
              ))}
            </View>
          )}

          {/* Bug Fixes */}
          {rn.bug_fixes.length > 0 && (
            <View style={ps.section}>
              <View style={ps.sectionHead}>
                <View style={[ps.sectionDot, { backgroundColor: RED }]} />
                <Text style={[ps.sectionLbl, { color: RED }]}>Bug Fixes</Text>
              </View>
              {rn.bug_fixes.map((item, i) => (
                <View key={i} style={ps.item}>
                  <View style={[ps.bullet, { backgroundColor: RED }]} />
                  <Text style={ps.itemTxt}>{item}</Text>
                </View>
              ))}
            </View>
          )}

          {/* Footer note */}
          {rn.note && (
            <View style={ps.noteBox}>
              <Text style={{ fontSize: 10, color: AMBER }}>💡</Text>
              <Text style={ps.noteTxt}>{rn.note}</Text>
            </View>
          )}
        </View>

        {/* Footer — pinned to the bottom, repeats on every page */}
        <View style={ps.footer} fixed>
          <Text style={ps.footerTxt}>BusinessLocating · Release Notes · {dateStr}</Text>
          <Text
            style={ps.footerTxt}
            render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
          />
        </View>
      </Page>
    </Document>
  );
}

// ── Inline list editor ────────────────────────────────────────────────
function ListEditor({ label, color, items, onChange }: {
  label: string; color: string; items: string[]; onChange: (v: string[]) => void;
}) {
  const [draft, setDraft] = useState("");
  const add = () => {
    if (!draft.trim()) return;
    onChange([...items, draft.trim()]);
    setDraft("");
  };
  return (
    <div className="space-y-2">
      <Label className="text-xs font-bold uppercase tracking-widest" style={{ color }}>{label}</Label>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2 rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
            <span className="flex-1">{it}</span>
            <button onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500">
              <X className="h-3.5 w-3.5" />
            </button>
          </li>
        ))}
      </ul>
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder={`Add ${label.toLowerCase()} item…`}
          className="text-sm"
        />
        <Button type="button" size="sm" onClick={add} variant="outline">
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// ── Section (used inside modal) ───────────────────────────────────────
function Section({ icon, color, label, items }: { icon: React.ReactNode; color: string; label: string; items: string[] }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <span style={{ color }}>{icon}</span>
        <span className="text-xs font-bold uppercase tracking-widest" style={{ color }}>{label}</span>
      </div>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────
function ReleaseNoteModal({ rn, onClose, onDelete, onUpdate, canDelete, client }: {
  rn: ReleaseNote; onClose: () => void; onDelete: () => void;
  onUpdate: (updated: ReleaseNote) => void; canDelete: boolean; client: ApiClient;
}) {
  const [downloading, setDownloading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    version: rn.version,
    title: rn.title,
    released_at: rn.released_at,
    whats_new: [...rn.whats_new],
    improvements: [...rn.improvements],
    bug_fixes: [...rn.bug_fixes],
    note: rn.note ?? "",
  });
  const toast = useToast();
  const dateStr = new Date(rn.released_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const logo = await loadLogoDataUri();
      const blob = await pdf(<ReleaseNotePDF rn={rn} logo={logo} />).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `release-notes-v${rn.version}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  const handleSave = async () => {
    if (!form.version.trim() || !form.title.trim()) {
      toast.error("Version and title are required");
      return;
    }
    setSaving(true);
    try {
      const updated = await client.put<ReleaseNote>(`/release-notes/${rn.id}`, {
        version: form.version.trim(),
        title: form.title.trim(),
        released_at: form.released_at,
        whats_new: form.whats_new,
        improvements: form.improvements,
        bug_fixes: form.bug_fixes,
        note: form.note.trim() || null,
      });
      onUpdate(updated);
      setEditing(false);
      toast.success(`v${updated.version} updated`);
    } catch {
      toast.error("Failed to update release note");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg max-h-[90dvh] flex flex-col rounded-3xl bg-white shadow-2xl overflow-hidden">

        {/* Navy header */}
        <div className="bg-primary px-6 py-4 flex items-center justify-between gap-4 shrink-0">
          <div className="rounded-lg bg-white px-3 py-1.5">
            <BrandLogo className="h-7 w-auto object-contain" />
          </div>
          <div className="flex items-center gap-2">
            {canDelete && !editing && (
              <button
                onClick={() => setEditing(true)}
                className="rounded-full p-1.5 text-primary-foreground/60 hover:text-primary-foreground hover:bg-black/10 transition-colors"
                title="Edit release note"
              >
                <Pencil className="h-4 w-4" />
              </button>
            )}
            <button onClick={onClose} className="rounded-full p-1.5 text-primary-foreground/60 hover:text-primary-foreground hover:bg-black/10 transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {editing ? (
          /* ── Edit form ── */
          <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Version *</Label>
                <Input placeholder="e.g. 2.4.0" value={form.version} onChange={e => setForm(f => ({ ...f, version: e.target.value }))} />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs">Title *</Label>
                <Input placeholder="Release title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Release Date *</Label>
                <Input type="date" value={form.released_at} onChange={e => setForm(f => ({ ...f, released_at: e.target.value }))} />
              </div>
            </div>
            <ListEditor label="What's New" color="#16A34A" items={form.whats_new} onChange={v => setForm(f => ({ ...f, whats_new: v }))} />
            <ListEditor label="Improvements" color="#2563EB" items={form.improvements} onChange={v => setForm(f => ({ ...f, improvements: v }))} />
            <ListEditor label="Bug Fixes" color="#DC2626" items={form.bug_fixes} onChange={v => setForm(f => ({ ...f, bug_fixes: v }))} />
            <div className="space-y-1.5">
              <Label className="text-xs">Footer Note (optional)</Label>
              <Input placeholder="e.g. Clear cache if display issues." value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
            </div>
          </div>
        ) : (
          <>
            {/* Version banner */}
            <div className="bg-primary/5 border-b border-gray-100 px-6 py-4 flex items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="rounded-full bg-primary px-2.5 py-0.5 text-[0.65rem] font-bold text-primary-foreground">v{rn.version}</span>
                  <span className="text-xs text-gray-400">{dateStr}</span>
                </div>
                <h2 className="font-extrabold text-gray-900 text-lg leading-snug">{rn.title}</h2>
              </div>
              <Flame className="h-8 w-8 text-primary/20 shrink-0" />
            </div>

            {/* Scrollable content */}
            <div className="overflow-y-auto flex-1 px-6 py-5 space-y-6">
              {rn.whats_new.length > 0 && (
                <Section icon={<Sparkles className="h-4 w-4" />} color="#16A34A" label="What's New" items={rn.whats_new} />
              )}
              {rn.improvements.length > 0 && (
                <Section icon={<Lightbulb className="h-4 w-4" />} color="#2563EB" label="Improvements" items={rn.improvements} />
              )}
              {rn.bug_fixes.length > 0 && (
                <Section icon={<Bug className="h-4 w-4" />} color="#DC2626" label="Bug Fixes" items={rn.bug_fixes} />
              )}
              {rn.note && (
                <div className="rounded-xl bg-amber-500/10 border border-amber-500/25 px-4 py-3 flex items-start gap-2">
                  <StickyNote className="h-4 w-4 shrink-0 text-amber-500 mt-0.5" />
                  <p className="text-sm text-amber-600">{rn.note}</p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Footer actions */}
        <div className="shrink-0 border-t border-gray-100 px-6 py-4 flex items-center justify-between gap-3 bg-gray-50/60">
          {editing ? (
            <>
              <Button onClick={handleSave} disabled={saving} className="gap-2">
                {saving ? "Saving…" : "Save Changes"}
              </Button>
              <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
            </>
          ) : (
            <>
              <Button onClick={handleDownload} disabled={downloading} className="gap-2">
                <Download className="h-4 w-4" />
                {downloading ? "Generating PDF…" : "Download PDF"}
              </Button>
              <div className="flex gap-2">
                {canDelete && (
                  <Button variant="outline" onClick={onDelete} className="gap-2 text-red-600 border-red-500/30 hover:bg-red-500/10">
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </Button>
                )}
                <Button variant="outline" onClick={onClose}>Close</Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Compact list row ──────────────────────────────────────────────────
function ReleaseNoteCard({ rn, onDelete, onUpdate, isLatest, canDelete, client }: {
  rn: ReleaseNote; onDelete: () => void; onUpdate: (updated: ReleaseNote) => void;
  isLatest: boolean; canDelete: boolean; client: ApiClient;
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [current, setCurrent] = useState(rn);
  const dateStr = new Date(current.released_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  const totalItems = current.whats_new.length + current.improvements.length + current.bug_fixes.length;

  const handleUpdate = (updated: ReleaseNote) => {
    setCurrent(updated);
    onUpdate(updated);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        className={`w-full flex items-center justify-between gap-4 rounded-2xl border bg-white px-5 py-4 text-left shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all ${isLatest ? "border-emerald-500/30" : "border-gray-100"}`}
      >
        {/* Left */}
        <div className="flex items-center gap-3 min-w-0">
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${isLatest ? "bg-emerald-500/15" : "bg-primary/10"}`}>
            <Flame className={`h-5 w-5 ${isLatest ? "text-emerald-600" : "text-primary"}`} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="rounded-full bg-primary px-2.5 py-0.5 text-[0.65rem] font-bold text-primary-foreground">v{current.version}</span>
              {isLatest && (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-[0.65rem] font-bold text-emerald-600 border border-emerald-500/30">
                  <Sparkles className="h-2.5 w-2.5" /> Latest
                </span>
              )}
              <span className="text-xs text-gray-400">{dateStr}</span>
            </div>
            <p className="mt-0.5 font-bold text-gray-900 truncate">{current.title}</p>
          </div>
        </div>

        {/* Right */}
        <div className="flex items-center gap-3 shrink-0">
          {totalItems > 0 && (
            <span className="hidden sm:inline-flex items-center gap-1 text-xs text-gray-400">
              <ChevronDown className="h-3.5 w-3.5 -rotate-90" />
              {totalItems} change{totalItems !== 1 ? "s" : ""}
            </span>
          )}
          <ChevronDown className="h-4 w-4 text-gray-400 -rotate-90" />
        </div>
      </button>

      {/* Modal */}
      {modalOpen && (
        <ReleaseNoteModal
          rn={current}
          onClose={() => setModalOpen(false)}
          canDelete={canDelete}
          client={client}
          onDelete={() => { setModalOpen(false); onDelete(); }}
          onUpdate={handleUpdate}
        />
      )}
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────
const EMPTY_FORM = {
  version: "", title: "", released_at: new Date().toISOString().slice(0, 10),
  whats_new: [] as string[], improvements: [] as string[], bug_fixes: [] as string[], note: "",
};

const EXAMPLE_FORM = {
  version: "2.4.0",
  title: "Trending Listings & BL Advisor Enhancements",
  released_at: new Date().toISOString().slice(0, 10),
  whats_new: [
    "Trending listings carousel on buyer home page ranked by unique viewer count",
    "Potential Commission slider on listing detail page",
    "Release Notes section for platform changelog",
  ],
  improvements: [
    "Agent detail card renamed to BL Advisor with cleaner layout",
    "Asking price card shows live commission calculator (1–10%)",
    "Admin listings page shows trending carousel at the top",
  ],
  bug_fixes: [
    "Fixed Mail Agent button text colour override by browser defaults",
    "Fixed Export PDF button incorrectly showing in sidebar",
  ],
  note: "Clear your cache if any layout looks off.",
};


export interface AdminReleaseNotesPageProps {
  /** Which console's client to talk to. Defaults to the portal's. */
  client?: ApiClient;
  /** Create / edit / delete. False in the portal console: cutting a release is
   *  a super admin action now. The API enforces it independently — hiding the
   *  controls is courtesy, not the gate. */
  canManage?: boolean;
}

export default function AdminReleaseNotesPage({
  client = api,
  canManage = false,
}: AdminReleaseNotesPageProps = {}) {

  const [notes, setNotes] = useState<ReleaseNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  useEffect(() => {
    client.get<ReleaseNote[]>("/release-notes")
      .then(setNotes)
      .catch(() => setNotes([]))   // table may not exist yet — just show empty state
      .finally(() => setLoading(false));
  }, [client]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async () => {
    if (!form.version.trim() || !form.title.trim()) {
      toast.error("Version and title are required");
      return;
    }
    setSaving(true);
    try {
      const created = await client.post<ReleaseNote>("/release-notes", {
        version: form.version.trim(),
        title: form.title.trim(),
        released_at: form.released_at,
        whats_new: form.whats_new,
        improvements: form.improvements,
        bug_fixes: form.bug_fixes,
        note: form.note.trim() || null,
      });
      setNotes([created, ...notes]);
      setForm({ ...EMPTY_FORM });
      setShowForm(false);
      toast.success(`v${created.version} created`);
    } catch {
      toast.error("Failed to create release note");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this release note?")) return;
    try {
      await client.delete(`/release-notes/${id}`);
      setNotes(notes.filter(n => n.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleUpdate = (updated: ReleaseNote) => {
    setNotes(prev => prev.map(n => n.id === updated.id ? updated : n));
  };

  return (
    <PageShell>
      <PageHero
        eyebrow="Admin"
        title="Release Notes"
        description={
          canManage
            ? "Manage version history and changelogs published to the platform."
            : "Version history and changelogs published to the platform. Cutting a new release moved to the super admin console."
        }
        actions={
          canManage ? (
            <Button onClick={() => setShowForm(!showForm)} className="gap-2">
              <Plus className="h-4 w-4" />
              New Release
            </Button>
          ) : undefined
        }
        aside={
          <div>
            <h2 className="text-lg font-bold text-foreground">Changelog snapshot</h2>
            <div className="mt-3 grid grid-cols-2 gap-2.5">
              <div className="public-glass-card rounded-xl p-3">
                <div className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Releases</div>
                <div className="mt-1.5 text-xl font-extrabold text-foreground">{notes.length}</div>
              </div>
              <div className="public-glass-card rounded-xl p-3">
                <div className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Latest</div>
                <div className="mt-1.5 text-xl font-extrabold text-primary">{notes[0]?.version ? `v${notes[0].version}` : "—"}</div>
              </div>
            </div>
          </div>
        }
      />

      <PagePanel>
        <PanelHeading
          title="Version history"
          description={`${notes.length} release${notes.length === 1 ? "" : "s"} published`}
        />

        {/* Create Form — portal admin only */}
        {canManage && showForm && (
          <div className="mb-6 rounded-2xl border border-primary/20 bg-primary/5 p-5 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-primary text-sm uppercase tracking-widest">New Release Note</h3>
              <button
                type="button"
                onClick={() => setForm({ ...EXAMPLE_FORM })}
                className="inline-flex items-center gap-1.5 rounded-lg border border-primary/20 bg-white px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/5 transition-colors"
              >
                <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                Load Example
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Version *</Label>
                <Input placeholder="e.g. 2.4.0" value={form.version} onChange={e => setForm(f => ({ ...f, version: e.target.value }))} />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs">Title *</Label>
                <Input placeholder="e.g. E-commerce Website Release" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Release Date *</Label>
                <Input type="date" value={form.released_at} onChange={e => setForm(f => ({ ...f, released_at: e.target.value }))} />
              </div>
            </div>

            <ListEditor label="What's New" color="#16A34A" items={form.whats_new} onChange={v => setForm(f => ({ ...f, whats_new: v }))} />
            <ListEditor label="Improvements" color="#2563EB" items={form.improvements} onChange={v => setForm(f => ({ ...f, improvements: v }))} />
            <ListEditor label="Bug Fixes" color="#DC2626" items={form.bug_fixes} onChange={v => setForm(f => ({ ...f, bug_fixes: v }))} />

            <div className="space-y-1.5">
              <Label className="text-xs">Footer Note (optional)</Label>
              <Input placeholder="e.g. Clear your cache if you face any display issues." value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
            </div>

            <div className="flex gap-2 pt-1">
              <Button onClick={handleCreate} disabled={saving}>{saving ? "Saving…" : "Create Release Note"}</Button>
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="py-12 text-center text-sm text-gray-400">Loading…</div>
        ) : notes.length === 0 ? (
          <EmptyPanel
            icon={Flame}
            title="No release notes yet"
            description={
              canManage
                ? "Create your first release note to start the platform changelog."
                : "The super admin has not published a release note yet."
            }
          />
        ) : (
          <div className="space-y-4">
            {notes.map((rn, idx) => (
              <ReleaseNoteCard
                key={rn.id}
                rn={rn}
                isLatest={idx === 0}
                canDelete={canManage}
                client={client}
                onDelete={() => handleDelete(rn.id)}
                onUpdate={handleUpdate}
              />
            ))}
          </div>
        )}
      </PagePanel>
    </PageShell>
  );
}
